import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Container, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Field, ErrorMessage, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { FaPhone, FaLock, FaSignInAlt, FaGraduationCap, FaVideo, FaCertificate, FaUserPlus } from 'react-icons/fa';
import 'animate.css';
import './Auth.css';

interface LoginFormValues {
  phoneNumber: string;
  password: string;
}

const Login: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [animate, setAnimate] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // تفعيل التحريكات بعد تحميل الصفحة
    setTimeout(() => {
      setAnimate(true);
    }, 100);

    // التحقق مما إذا كان المستخدم مسجل الدخول بالفعل
    const token = localStorage.getItem('token');
    if (token) {
      const userRole = localStorage.getItem('userRole');
      if (userRole === 'admin') {
        navigate('/admin/dashboard');
      } else if (userRole === 'teacher') {
        navigate('/teacher/dashboard');
      } else {
        navigate('/');
      }
    }
  }, [navigate]);

  const initialValues: LoginFormValues = {
    phoneNumber: '',
    password: '',
  };

  const validationSchema = Yup.object({
    phoneNumber: Yup.string()
      .required('رقم الهاتف مطلوب')
      .matches(/^[0-9]+$/, 'رقم الهاتف يجب أن يحتوي على أرقام فقط')
      .min(10, 'رقم الهاتف غير صالح')
      .max(15, 'رقم الهاتف غير صالح'),
    password: Yup.string()
      .required('كلمة المرور مطلوبة')
  });

  const handleSubmit = async (values: LoginFormValues, { setSubmitting }: any) => {
    try {
      setError(null);
      setLoading(true);
      await signIn(values.phoneNumber, values.password);
      
      // التحقق من دور المستخدم من localStorage
      const userRole = localStorage.getItem('userRole');
      const token = localStorage.getItem('token');
      
      // التأكد من وجود token قبل التوجيه
      if (token) {
        // توجيه المستخدم بناءً على دوره
        if (userRole === 'admin') {
          navigate('/admin/dashboard');
        } else if (userRole === 'teacher') {
          navigate('/teacher/dashboard');
        } else {
          navigate('/'); // الطالب أو أي دور آخر
        }
      } else {
        throw new Error('فشل تسجيل الدخول، يرجى المحاولة مرة أخرى');
      }
    } catch (err: any) {
      setError(err.message || 'حدث خطأ أثناء تسجيل الدخول');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background"></div>
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100 py-5">
          <Col md={10} lg={8} xl={7} className="px-3 px-md-4">
            <Card className={`auth-card border-0 shadow-lg ${animate ? 'animate__animated animate__fadeIn' : ''}`}>
              <Card.Header className="text-center py-4 auth-header">
                <div className="logo-container mb-3">
                  <FaGraduationCap size={40} className="logo-icon" />
                </div>
                <h2 className="mb-0 fw-bold">تسجيل الدخول</h2>
              </Card.Header>
              <Card.Body className="p-4 p-lg-5">
                <Row>
                  <Col lg={5} className="d-none d-lg-block pe-lg-4">
                    <div className={`text-center mb-5 ${animate ? 'animate__animated animate__fadeInRight animate__delay-1s' : ''}`}>
                      <div className="auth-icon-container mb-4">
                        <FaSignInAlt size={40} className="auth-icon" />
                        <div className="auth-icon-ring"></div>
                      </div>
                      <h4 className="fw-bold mb-3">مرحباً بك مجدداً</h4>
                      <p className="text-muted mb-4">سعداء بعودتك إلى منصتنا التعليمية</p>
                    </div>
                    
                    <div className={`auth-features ${animate ? 'animate__animated animate__fadeInUp animate__delay-1s' : ''}`}>
                      <div className="auth-feature mb-3">
                        <FaGraduationCap className="text-primary me-2" />
                        <span>دورات تعليمية متميزة</span>
                      </div>
                      <div className="auth-feature mb-3">
                        <FaVideo className="text-primary me-2" />
                        <span>محتوى مرئي عالي الجودة</span>
                      </div>
                      <div className="auth-feature mb-3">
                        <FaCertificate className="text-primary me-2" />
                        <span>شهادات معتمدة</span>
                      </div>
                    </div>
                  </Col>
                  
                  <Col lg={7} className="ps-lg-4">
                    <div className={`${animate ? 'animate__animated animate__fadeInLeft animate__delay-1s' : ''}`}>
                      {error && <Alert variant="danger" className="animate__animated animate__shakeX">{error}</Alert>}

                      <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                      >
                        {({ handleSubmit, isSubmitting, touched, errors }) => (
                          <FormikForm onSubmit={handleSubmit}>
                            <div className="mb-4">
                              <Form.Group controlId="phoneNumber">
                                <Form.Label className="fw-bold">رقم الهاتف</Form.Label>
                                <div className={`input-group auth-input-group ${touched.phoneNumber && errors.phoneNumber ? 'is-invalid' : ''}`}>
                                  <span className="input-group-text">
                                    <FaPhone className="text-primary" />
                                  </span>
                                  <Field
                                    as={Form.Control}
                                    type="text"
                                    name="phoneNumber"
                                    placeholder="أدخل رقم الهاتف"
                                    className={`py-2 auth-input ${touched.phoneNumber && errors.phoneNumber ? 'is-invalid' : ''}`}
                                  />
                                </div>
                                <ErrorMessage
                                  name="phoneNumber"
                                  component="div"
                                  className="text-danger mt-1 error-message"
                                />
                              </Form.Group>
                            </div>

                            <div className="mb-3">
                              <Form.Group controlId="password">
                                <Form.Label className="fw-bold">كلمة المرور</Form.Label>
                                <div className={`input-group auth-input-group ${touched.password && errors.password ? 'is-invalid' : ''}`}>
                                  <span className="input-group-text">
                                    <FaLock className="text-primary" />
                                  </span>
                                  <Field
                                    as={Form.Control}
                                    type="password"
                                    name="password"
                                    placeholder="أدخل كلمة المرور"
                                    className={`py-2 auth-input ${touched.password && errors.password ? 'is-invalid' : ''}`}
                                  />
                                </div>
                                <ErrorMessage
                                  name="password"
                                  component="div"
                                  className="text-danger mt-1 error-message"
                                />
                              </Form.Group>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-4">
                              <Form.Check
                                type="checkbox"
                                id="rememberMe"
                                label="تذكرني"
                                className="mb-0"
                              />
                              <Link to="/forgot-password" className="text-primary fw-bold text-decoration-none forgot-link">نسيت كلمة المرور؟</Link>
                            </div>

                            <Button
                              variant="primary"
                              type="submit"
                              className="w-100 py-2 mt-2 mb-3 d-flex align-items-center justify-content-center login-button"
                              disabled={loading || isSubmitting}
                            >
                              {loading ? (
                                <div className="spinner-container">
                                  <div className="spinner"></div>
                                  <span className="ms-2">جاري تسجيل الدخول...</span>
                                </div>
                              ) : (
                                <>
                                  <FaSignInAlt className="me-2" />
                                  تسجيل الدخول
                                </>
                              )}
                            </Button>

                            <div className="text-center mt-4 pt-3 border-top">
                              <p className="mb-0">
                                ليس لديك حساب؟{' '}
                                <Link to="/register" className="text-primary fw-bold text-decoration-none register-link">إنشاء حساب جديد</Link>
                              </p>
                            </div>
                          </FormikForm>
                        )}
                      </Formik>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="bg-light text-center py-3">
                <p className="text-muted mb-0"> {new Date().getFullYear()} منصة السامي التعليمية. جميع الحقوق محفوظة</p>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Login;
